import {
  createSketchWallGeometryContext,
  sketchWallOtherNodeId,
  sketchWallDirection,
  sketchLineIntersection,
  mitered,
  sketchWallCornerAtNode as sketchWallCornerAtNodeForGeometry,
  sketchWallCorners as sketchWallCornersForGeometry
} from '../../plugin-sdk/sketch_wall_geometry.js';
import { S } from '../app/app_state.js?v=20260817c';
import { uid, normalizeProjectLevels } from '../utils/common_constants.js?v=20260706d';

const SKETCH_WALL_DEFAULT_THICKNESS = 150;
const SKETCH_WALL_DEFAULT_HEIGHT = 2500;
const SKETCH_WALL_MIN_LENGTH = 20;
const SKETCH_WALL_JOIN_TOLERANCE = 40; // mm — click/draw snap distance onto an existing node
const SKETCH_WALL_FURRING_DEFAULT_THICKNESS = 100;
const SKETCH_WALL_FURRING_DEFAULT_HEIGHT = 1500;
const SKETCH_WALL_SYSTEM_ID = 'core_sketch_walls';

let ensuredSketchWallProject = null;
let sketchStateIndexCache = null;

// Keep authored and measured angles at full precision; rounding is presentation only.
function sketchExactAngleRadians(radians) {
  return Number.isFinite(radians) ? radians : null;
}

function ensureSketchWallState() {
  if (!S.project || typeof S.project !== 'object') S.project = {};
  const sw = S.project.sketchWalls;
  if (!sw || typeof sw !== 'object') {
    S.project.sketchWalls = { nodes: [], walls: [], angles: [], straightJoints: [] };
  } else {
    if (!Array.isArray(sw.nodes)) sw.nodes = [];
    if (!Array.isArray(sw.walls)) sw.walls = [];
    if (!Array.isArray(sw.angles)) sw.angles = [];
    if (!Array.isArray(sw.straightJoints)) sw.straightJoints = [];
    if (ensuredSketchWallProject !== sw) {
      ensuredSketchWallProject = sw;
      sketchStateIndexCache = null;
    }
    for (const wall of sw.walls) {
      if (!wall) continue;
      if (!wall.fixed) {
        wall.fixedSource = null;
      } else if (!['chain', 'user', 'axis'].includes(wall.fixedSource)) {
        if (wall.horizontal || wall.vertical) wall.fixedSource = 'axis';
        else {
          const startFixed = sw.nodes.some(node => node.id === wall.startNodeId && node.fixed);
          const endFixed = sw.nodes.some(node => node.id === wall.endNodeId && node.fixed);
          wall.fixedSource = startFixed || endFixed ? 'chain' : 'user';
        }
      }
    }
  }
  if (!S.selection || typeof S.selection !== 'object') S.selection = {};
  if (!S.selection.sketchWall || typeof S.selection.sketchWall !== 'object') {
    S.selection.sketchWall = { nodeId: null, wallId: null, angleId: null };
  }
  if (!Array.isArray(S.selection.sketchWallIds)) {
    S.selection.sketchWallIds = S.selection.sketchWall.wallId
      ? [S.selection.sketchWall.wallId]
      : [];
  }
  if (!S.ui || typeof S.ui !== 'object') S.ui = {};
  if (!S.ui.sketchWallDraw || typeof S.ui.sketchWallDraw !== 'object') {
    S.ui.sketchWallDraw = { startNodeId: null, currentPoint: null };
  }
  if (typeof S.ui.sketchWallTool !== 'string') S.ui.sketchWallTool = null;
  if (typeof S.ui.sketchWallActiveSidebarCard !== 'string') S.ui.sketchWallActiveSidebarCard = 'main';
  if (!S.ui.sketchWallDrawDefaults || typeof S.ui.sketchWallDrawDefaults !== 'object') {
    const roomHeight = normalizeProjectLevels(S.project?.levels).rh;
    S.ui.sketchWallDrawDefaults = {
      thickness: SKETCH_WALL_DEFAULT_THICKNESS,
      height: roomHeight || SKETCH_WALL_DEFAULT_HEIGHT,
      hatchType: 'beton2',
      wallColor: null
    };
  }
  return S.project.sketchWalls;
}

function sketchStateIndexes() {
  const state = ensureSketchWallState();
  if (sketchStateIndexCache
    && sketchStateIndexCache.state === state
    && sketchStateIndexCache.nodes === state.nodes
    && sketchStateIndexCache.walls === state.walls
    && sketchStateIndexCache.nodeCount === state.nodes.length
    && sketchStateIndexCache.wallCount === state.walls.length) return sketchStateIndexCache;
  const nodesById = new Map(state.nodes.map(node => [node.id, node]));
  const wallsById = new Map(state.walls.map(wall => [wall.id, wall]));
  const wallsByNodeId = new Map();
  for (const wall of state.walls) {
    for (const nodeId of [wall.startNodeId, wall.endNodeId]) {
      const entries = wallsByNodeId.get(nodeId) || [];
      entries.push(wall);
      wallsByNodeId.set(nodeId, entries);
    }
  }
  sketchStateIndexCache = {
    state,
    nodes: state.nodes,
    walls: state.walls,
    nodeCount: state.nodes.length,
    wallCount: state.walls.length,
    nodesById,
    wallsById,
    wallsByNodeId
  };
  return sketchStateIndexCache;
}

// Explicit, non-mutating geometry reads for one drawing/project. The caller
// creates a fresh context after geometry changes; no index survives a query.
// Model records belong to the supplied snapshot, while returned lists are
// separate immutable arrays. Nothing is initialized in the project or UI.
function createSketchWallReadContext(project) {
  return createSketchWallGeometryContext(project?.sketchWalls);
}

// Retained load hook for compatibility. Decimal targets must survive load/undo unchanged.
function normalizeSketchWallStoredAngles() {
  return false;
}

// Lets the Details/Farbe cards be edited before the first wall of a chain
// exists (clicking "Wand zeichnen" arms the tool with nothing selected yet)
// — every wall subsequently drawn while the tool stays armed picks these up
// as its initial thickness/height/hatchType/wallColor (see createSketchWall's
// call site in sketch_wall_interaction.js).
function sketchWallDrawDefaults() {
  ensureSketchWallState();
  return S.ui.sketchWallDrawDefaults;
}

function updateSketchWallDrawDefaults(patch) {
  ensureSketchWallState();
  Object.assign(S.ui.sketchWallDrawDefaults, patch);
}

function allSketchNodes() {
  return ensureSketchWallState().nodes;
}

function allSketchWalls() {
  return ensureSketchWallState().walls;
}

function allSketchAngles() {
  return ensureSketchWallState().angles;
}

function allSketchStraightJoints() {
  return ensureSketchWallState().straightJoints;
}

function sketchNodeById(id) {
  return sketchStateIndexes().nodesById.get(id) || null;
}

function sketchWallById(id) {
  return sketchStateIndexes().wallsById.get(id) || null;
}

function sketchAngleById(id) {
  return allSketchAngles().find(angle => angle.id === id) || null;
}

function sketchWallsAtNode(nodeId) {
  return sketchStateIndexes().wallsByNodeId.get(nodeId) || [];
}

// Every node reachable from `startWall`'s two endpoints by walking shared
// walls — i.e. the rigid physical body that wall is actually part of.
// Dragging a wall body should only move this set, not the whole sketch:
// two chains that don't share a node aren't connected and must stay
// independently movable.
function sketchWallConnectedNodeIds(startWall) {
  const visited = new Set([startWall.startNodeId, startWall.endNodeId]);
  const queue = [...visited];
  while (queue.length) {
    const nodeId = queue.shift();
    for (const wall of sketchWallsAtNode(nodeId)) {
      const otherId = sketchWallOtherNodeId(wall, nodeId);
      if (!visited.has(otherId)) {
        visited.add(otherId);
        queue.push(otherId);
      }
    }
  }
  return visited;
}

function sketchNodeDistance(nodeA, nodeB) {
  return Math.hypot(nodeB.x - nodeA.x, nodeB.z - nodeA.z);
}

function sketchWallLength(wall) {
  const a = sketchNodeById(wall.startNodeId);
  const b = sketchNodeById(wall.endNodeId);
  if (!a || !b) return 0;
  return sketchNodeDistance(a, b);
}

// Signed interior angle (radians) at `nodeId` between wallA and wallB,
// measured from wallA's outgoing direction to wallB's outgoing direction.
function sketchAngleValueAt(nodeId, wallAId, wallBId) {
  const node = sketchNodeById(nodeId);
  const wallA = sketchWallById(wallAId);
  const wallB = sketchWallById(wallBId);
  if (!node || !wallA || !wallB) return 0;
  const otherA = sketchNodeById(sketchWallOtherNodeId(wallA, nodeId));
  const otherB = sketchNodeById(sketchWallOtherNodeId(wallB, nodeId));
  if (!otherA || !otherB) return 0;
  const angleA = Math.atan2(otherA.z - node.z, otherA.x - node.x);
  const angleB = Math.atan2(otherB.z - node.z, otherB.x - node.x);
  let delta = angleB - angleA;
  while (delta <= -Math.PI) delta += Math.PI * 2;
  while (delta > Math.PI) delta -= Math.PI * 2;
  return delta;
}

// The visible arc is directed from wallA to wallB and therefore lives in
// 0..360°, while the solver uses the equivalent signed -180..180° value.
// Reading the directed value from geometry keeps labels/editors honest even
// when an older stored target used an equivalent form such as -270° for 90°.
function sketchDirectedAngleValueAt(nodeId, wallAId, wallBId) {
  const signed = sketchAngleValueAt(nodeId, wallAId, wallBId);
  return signed < 0 ? signed + Math.PI * 2 : signed;
}

function findNearestSketchNode(point, tolerance = SKETCH_WALL_JOIN_TOLERANCE) {
  let best = null;
  let bestDist = tolerance;
  for (const node of allSketchNodes()) {
    const dist = Math.hypot(point.x - node.x, point.z - node.z);
    if (dist <= bestDist) {
      bestDist = dist;
      best = node;
    }
  }
  return best;
}

// Perpendicular projection of `point` onto the nearest wall's centerline,
// clamped away from its endpoints by SKETCH_WALL_MIN_LENGTH (a hit right at
// an endpoint should snap to that *node* instead, via findNearestSketchNode).
// Used to start/attach a wall mid-span — e.g. a T-Verbindung, which splits
// the wall it attaches to at exactly that point (see splitSketchWall).
function findNearestSketchWallPoint(point, tolerance = SKETCH_WALL_JOIN_TOLERANCE) {
  let best = null;
  let bestDist = tolerance;
  for (const wall of allSketchWalls()) {
    const a = sketchNodeById(wall.startNodeId);
    const b = sketchNodeById(wall.endNodeId);
    if (!a || !b) continue;
    const dx = b.x - a.x;
    const dz = b.z - a.z;
    const lenSq = dx * dx + dz * dz;
    const length = Math.sqrt(lenSq);
    if (length < SKETCH_WALL_MIN_LENGTH * 2) continue; // too short to usefully split
    let t = ((point.x - a.x) * dx + (point.z - a.z) * dz) / lenSq;
    const minT = SKETCH_WALL_MIN_LENGTH / length;
    t = Math.min(Math.max(t, minT), 1 - minT);
    const projX = a.x + dx * t;
    const projZ = a.z + dz * t;
    const dist = Math.hypot(point.x - projX, point.z - projZ);
    if (dist <= bestDist) {
      bestDist = dist;
      best = { wall, point: { x: projX, z: projZ } };
    }
  }
  return best;
}

function createSketchNode(x, z, fixed = false) {
  const node = { id: uid('sketch_node'), x, z, fixed: Boolean(fixed) };
  allSketchNodes().push(node);
  return node;
}

function createSketchWall(startNodeId, endNodeId, options = {}) {
  const roomHeight = normalizeProjectLevels(S.project?.levels).rh;
  const wall = {
    id: uid('sketch_wall'),
    startNodeId,
    endNodeId,
    thickness: Number(options.thickness) || SKETCH_WALL_DEFAULT_THICKNESS,
    height: Number(options.height) || roomHeight || SKETCH_WALL_DEFAULT_HEIGHT,
    hatchType: options.hatchType || 'beton2',
    wallColor: options.wallColor || null,
    length: null,
    lengthMode: null, // 'inner' | 'outer' — which face `length` targets
    horizontal: false,
    vertical: false,
    fixed: false,
    fixedSource: null, // 'chain' automatic anchor | 'user' explicit Fixiert | 'axis' horizontal/vertical
    fixedAngle: null, // absolute angle (radians) pinned when `fixed` is true
    free: true,
    conflict: false,
    openings: [],
    furring: defaultSketchWallFurring()
  };
  allSketchWalls().push(wall);
  syncSketchAnglesAtNode(startNodeId);
  syncSketchAnglesAtNode(endNodeId);
  return wall;
}

function sketchLiveNodePoint(nodeId) {
  const node = sketchNodeById(nodeId);
  return node ? { x: node.x, z: node.z } : { x: 0, z: 0 };
}

// Absolute direction angle (radians) of `wall`, start->end. Used both to
// capture the target when a wall is marked "fixed" and by the solver's
// matching residual — `getPoint` defaults to live node positions so it can
// also be called outside a solve (e.g. right when the user toggles fixed).
function sketchWallAbsoluteAngle(wall, getPoint = sketchLiveNodePoint) {
  const a = getPoint(wall.startNodeId);
  const b = getPoint(wall.endNodeId);
  return Math.atan2(b.z - a.z, b.x - a.x);
}

// The live host supplies topology; pure geometry is shared with snapshot readers.
const sketchWallLiveGeometryContext = Object.freeze({
  wallsAtNode: sketchWallsAtNode,
  allStraightJoints: allSketchStraightJoints
});

function sketchWallCornerAtNode(wall, nodeId, getPoint, side, geometryContext = null) {
  return sketchWallCornerAtNodeForGeometry(
    wall, nodeId, getPoint, side, geometryContext || sketchWallLiveGeometryContext
  );
}

// Face length of `wall` on `side` (+1 = inner/yellow, -1 = outer/orange):
// the distance between its two mitered corner points on that side.
function sketchWallSideLength(wall, getPoint, side) {
  const a = sketchWallCornerAtNode(wall, wall.startNodeId, getPoint, side);
  const b = sketchWallCornerAtNode(wall, wall.endNodeId, getPoint, side);
  return Math.hypot(b.x - a.x, b.z - a.z);
}

function sketchWallCorners(wall, getPoint, geometryContext = null) {
  return sketchWallCornersForGeometry(wall, getPoint, geometryContext || sketchWallLiveGeometryContext);
}

// Like sketchWallCornerAtNode, but for an arbitrary point *along* the wall
// (not just its two node ends) — a plain perpendicular offset from the
// centerline, never mitered (mitering only makes sense where this wall
// actually meets another one at a shared node). Used to cut the internal
// jamb/sill/lintel faces an Ausschnitt (opening) needs.
function sketchPointAtOffsetOnWall(wall, offset, side, getPoint) {
  const start = getPoint(wall.startNodeId);
  const dir = sketchWallDirection(wall, getPoint);
  const half = (Number(wall.thickness) || 0) / 2;
  const leftX = -dir.z;
  const leftZ = dir.x;
  return {
    x: start.x + dir.x * offset + side * half * leftX,
    z: start.z + dir.z * offset + side * half * leftZ
  };
}

const SKETCH_WALL_OPENING_PRESETS = Object.freeze({
  door: Object.freeze({ width: 900, height: 2000, bottomOffset: 0 }),
  window: Object.freeze({ width: 700, height: 1000, bottomOffset: 900 })
});

function allSketchWallOpenings(wall) {
  return Array.isArray(wall?.openings) ? wall.openings : [];
}

// Openings sorted left-to-right along the wall, each with its derived
// (never stored) "Ausschnitt N" label — numbering stays correct after
// deletions since it's recomputed from current order every time.
function sketchWallOpeningsWithLabels(wall) {
  return allSketchWallOpenings(wall)
    .slice()
    .sort((a, b) => a.offset - b.offset)
    .map((opening, index) => ({ ...opening, label: `Ausschnitt ${index + 1}` }));
}

// Clamps an opening's center offset so its full width stays within the
// wall's own current length — an opening may never stick out past its own
// wall's ends, and (by construction, since offset is always local to one
// wall) never crosses onto a neighboring wall either, even a collinear one.
function sketchWallOpeningClampedOffset(wall, width, offset) {
  const length = Math.max(SKETCH_WALL_MIN_LENGTH, sketchWallLength(wall));
  const halfWidth = Math.min(Number(width) / 2 || 0, length / 2);
  // `offset || length / 2` would silently treat a legitimate offset of
  // exactly 0 (e.g. a split-transferred opening rebased flush onto wallB's
  // own new start — see splitSketchWall) as "not specified" and re-center
  // it instead, since 0 is falsy — Number.isFinite tells a real 0 apart
  // from a genuinely missing/invalid offset.
  const numericOffset = Number(offset);
  return sketchClamp(Number.isFinite(numericOffset) ? numericOffset : length / 2, halfWidth, length - halfWidth);
}

function sketchClamp(value, min, max) {
  if (max < min) return (min + max) / 2;
  return Math.max(min, Math.min(max, value));
}

// Re-clamps every opening on `wall` against its current length — call
// after anything that can change wall.length (node drags via
// solveSketchWalls, updateSketchWallLength, ...) so a stored offset never
// silently drifts outside the wall once it's shortened.
function reclampSketchWallOpenings(wall) {
  for (const opening of allSketchWallOpenings(wall)) {
    opening.offset = sketchWallOpeningClampedOffset(wall, opening.width, opening.offset);
  }
}

function createSketchWallOpening(wallId, offset, preset = {}) {
  const wall = sketchWallById(wallId);
  if (!wall) return null;
  const spec = SKETCH_WALL_OPENING_PRESETS[preset] || preset;
  const width = Math.max(1, Number(spec.width) || SKETCH_WALL_OPENING_PRESETS.door.width);
  const height = Math.max(1, Number(spec.height) || SKETCH_WALL_OPENING_PRESETS.door.height);
  const bottomOffset = Math.max(0, Number(spec.bottomOffset) || 0);
  if (!Array.isArray(wall.openings)) wall.openings = [];
  const opening = {
    id: uid('sketch_opening'),
    offset: sketchWallOpeningClampedOffset(wall, width, offset),
    width,
    height,
    bottomOffset
  };
  wall.openings.push(opening);
  return opening;
}

function sketchWallOpeningById(wallId, openingId) {
  const wall = sketchWallById(wallId);
  return allSketchWallOpenings(wall).find(opening => opening.id === openingId) || null;
}

function updateSketchWallOpening(wallId, openingId, patch = {}) {
  const wall = sketchWallById(wallId);
  const opening = wall && allSketchWallOpenings(wall).find(entry => entry.id === openingId);
  if (!wall || !opening) return false;
  if (Number.isFinite(Number(patch.width)) && Number(patch.width) > 0) opening.width = Number(patch.width);
  if (Number.isFinite(Number(patch.height)) && Number(patch.height) > 0) opening.height = Number(patch.height);
  if (Number.isFinite(Number(patch.bottomOffset)) && Number(patch.bottomOffset) >= 0) opening.bottomOffset = Number(patch.bottomOffset);
  if (Number.isFinite(Number(patch.offset))) opening.offset = Number(patch.offset);
  opening.offset = sketchWallOpeningClampedOffset(wall, opening.width, opening.offset);
  return true;
}

function deleteSketchWallOpening(wallId, openingId) {
  const wall = sketchWallById(wallId);
  if (!wall || !Array.isArray(wall.openings)) return false;
  const before = wall.openings.length;
  wall.openings = wall.openings.filter(opening => opening.id !== openingId);
  return wall.openings.length !== before;
}

// Merges openings whose horizontal spans overlap or touch into combined
// regions — purely a 1D interval merge along the wall's length axis, used
// by the 2D "gap" overlay (sketch_wall_render2d.js), which only needs to
// know *where* along the wall material is missing, never at what height
// (plan view has no height axis). Two openings at different sill heights
// still show as one continuous gap here, which is correct for that view.
// For the actual 3D solid geometry, see sketchWallSolidPieces below — it
// does NOT reuse these regions, since collapsing height here as well would
// throw away exactly the information needed to keep two openings' true
// (possibly stepped/L-shaped) union instead of inflating to their
// smallest common bounding rectangle.
function sketchWallOpeningRegions(wall) {
  const spans = allSketchWallOpenings(wall)
    .map(opening => ({
      startOffset: opening.offset - opening.width / 2,
      endOffset: opening.offset + opening.width / 2
    }))
    .sort((a, b) => a.startOffset - b.startOffset);

  const regions = [];
  for (const span of spans) {
    const last = regions[regions.length - 1];
    if (last && span.startOffset <= last.endOffset) {
      last.endOffset = Math.max(last.endOffset, span.endOffset);
    } else {
      regions.push({ ...span });
    }
  }
  return regions;
}

// Merges horizontally-adjacent pieces that end up with identical vertical
// coverage (bottomY/topY) back together, so a wall with well-separated
// openings still emits one full-height pier between them instead of two
// artificially split at some interior opening's start/end offset that
// happens to also be a breakpoint elsewhere. Cosmetic/mesh-simplicity only
// — every piece is already geometrically correct without this.
function mergeAdjacentSketchWallSolidPieces(pieces) {
  const sorted = pieces.slice().sort((a, b) => a.startOffset - b.startOffset || a.bottomY - b.bottomY);
  const merged = [];
  for (const piece of sorted) {
    const match = merged.find(entry => (
      entry.endOffset === piece.startOffset && entry.bottomY === piece.bottomY && entry.topY === piece.topY
    ));
    if (match) match.endOffset = piece.endOffset;
    else merged.push({ ...piece });
  }
  return merged;
}

// The actual 3D cross-section decomposition: a plane-sweep over every
// opening's horizontal start/end offset, splitting the wall's length into
// strips within which the set of covering openings never changes. Within
// each strip, the openings' height intervals are merged (so a stacked pair
// — e.g. a transom above a door — becomes one tall gap) and then
// complemented against [0, heightOverride] to get that strip's solid
// piece(s). Unlike the old bounding-box-per-region approach, two openings
// that only partially overlap (different offsets AND different heights) now
// produce a true stepped/L-shaped union — each strip keeps its own exact
// sill/lintel — instead of both inflating to the smallest rectangle
// containing both. Returns only the solid (still-material) pieces; the
// openings themselves are simply the gaps between/among them.
//
// `heightOverride` defaults to the wall's own height (the normal case), but
// the Vormauerung reuses this exact same sweep capped at its own, usually
// shorter, height (see sketchWallFurringCrossSectionQuad's call site in
// sketch_wall_render3d.js) — an opening entirely above that cap is filtered
// out below (its clamped top ends up <= its bottom), so the Vormauerung
// only gets cut where an opening actually reaches down into it.
function sketchWallSolidPieces(wall, heightOverride = wall.height) {
  const wallHeight = Math.max(1, Number(heightOverride) || 0);
  const wallLength = Math.max(SKETCH_WALL_MIN_LENGTH, sketchWallLength(wall));
  const rects = allSketchWallOpenings(wall)
    .map(opening => ({
      start: sketchClamp(opening.offset - opening.width / 2, 0, wallLength),
      end: sketchClamp(opening.offset + opening.width / 2, 0, wallLength),
      bottom: Math.max(0, Number(opening.bottomOffset) || 0),
      top: Math.min(wallHeight, Math.max(0, Number(opening.bottomOffset) || 0) + Math.max(0, Number(opening.height) || 0))
    }))
    .filter(rect => rect.end - rect.start > 0.5 && rect.top - rect.bottom > 0.5);

  if (!rects.length) return [{ startOffset: 0, endOffset: wallLength, bottomY: 0, topY: wallHeight }];

  const breakpoints = [...new Set([0, wallLength, ...rects.flatMap(rect => [rect.start, rect.end])])]
    .filter(x => x >= 0 && x <= wallLength)
    .sort((a, b) => a - b);

  const pieces = [];
  for (let i = 0; i < breakpoints.length - 1; i++) {
    const stripStart = breakpoints[i];
    const stripEnd = breakpoints[i + 1];
    if (stripEnd - stripStart <= 0.5) continue;
    const stripMid = (stripStart + stripEnd) / 2;

    const openIntervals = rects
      .filter(rect => rect.start <= stripMid && rect.end >= stripMid)
      .map(rect => [rect.bottom, rect.top])
      .sort((a, b) => a[0] - b[0]);
    const mergedIntervals = [];
    for (const [bottom, top] of openIntervals) {
      const last = mergedIntervals[mergedIntervals.length - 1];
      if (last && bottom <= last[1]) last[1] = Math.max(last[1], top);
      else mergedIntervals.push([bottom, top]);
    }

    let cursorY = 0;
    for (const [bottom, top] of mergedIntervals) {
      if (bottom - cursorY > 0.5) pieces.push({ startOffset: stripStart, endOffset: stripEnd, bottomY: cursorY, topY: bottom });
      cursorY = Math.max(cursorY, top);
    }
    if (wallHeight - cursorY > 0.5) pieces.push({ startOffset: stripStart, endOffset: stripEnd, bottomY: cursorY, topY: wallHeight });
  }
  return mergeAdjacentSketchWallSolidPieces(pieces);
}

function defaultSketchWallFurring() {
  return {
    enabled: false,
    thickness: SKETCH_WALL_FURRING_DEFAULT_THICKNESS,
    height: SKETCH_WALL_FURRING_DEFAULT_HEIGHT,
    side: 1
    // wallColor is intentionally left unset here — sketchWallFurring()
    // below falls back to the main wall's own wallColor until the user
    // explicitly gives the Vormauerung its own (or resets it via "Gleich
    // wie Hauptwand"), so a freshly-enabled Vormauerung starts out looking
    // like a continuation of the wall, exactly like before this existed.
  };
}

// Safe read — never mutates the wall, so it also covers walls saved before
// this feature (or before it had its own color) existed. Mutators go
// through ensureSketchWallFurring below instead, which lazily creates the
// object the first time it's actually needed.
function sketchWallFurring(wall) {
  const raw = wall?.furring && typeof wall.furring === 'object' ? wall.furring : {};
  return {
    enabled: Boolean(raw.enabled),
    thickness: Number.isFinite(Number(raw.thickness)) && Number(raw.thickness) > 0
      ? Number(raw.thickness)
      : SKETCH_WALL_FURRING_DEFAULT_THICKNESS,
    height: Number.isFinite(Number(raw.height)) && Number(raw.height) > 0
      ? Number(raw.height)
      : SKETCH_WALL_FURRING_DEFAULT_HEIGHT,
    side: raw.side === -1 ? -1 : 1,
    wallColor: Object.prototype.hasOwnProperty.call(raw, 'wallColor') ? raw.wallColor : (wall?.wallColor ?? null)
  };
}

function ensureSketchWallFurring(wall) {
  if (!wall.furring || typeof wall.furring !== 'object') wall.furring = defaultSketchWallFurring();
  return wall.furring;
}

function setSketchWallFurringEnabled(id, enabled) {
  const wall = sketchWallById(id);
  if (!wall) return false;
  ensureSketchWallFurring(wall).enabled = Boolean(enabled);
  return true;
}

function updateSketchWallFurring(id, patch = {}) {
  const wall = sketchWallById(id);
  if (!wall) return false;
  const furring = ensureSketchWallFurring(wall);
  if (Number.isFinite(Number(patch.thickness)) && Number(patch.thickness) > 0) furring.thickness = Number(patch.thickness);
  if (Number.isFinite(Number(patch.height)) && Number(patch.height) > 0) furring.height = Number(patch.height);
  return true;
}

// The Vormauerung has no Wandart (texture pattern) of its own — it always
// renders with the main wall's hatchType, same as before — only its tint
// (wallColor) can differ, which is enough to tell it apart at a glance
// (e.g. a furring wall clad differently from the plastered wall around it)
// without a whole second texture-pattern picker.
function updateSketchWallFurringColor(id, wallColor) {
  const wall = sketchWallById(id);
  if (!wall) return false;
  ensureSketchWallFurring(wall).wallColor = wallColor || null;
  return true;
}

// "Gleich wie Hauptwand": resets the Vormauerung's own color back to
// whatever the main wall currently has — a one-time copy, not a live link,
// so it can be freely customized again afterwards.
function matchSketchWallFurringToMainWall(id) {
  const wall = sketchWallById(id);
  if (!wall) return false;
  ensureSketchWallFurring(wall).wallColor = wall.wallColor || null;
  return true;
}

// "Richtung ändern": flips which face of the wall (inner/+1 or outer/-1,
// the same side convention sketchWallCornerAtNode uses) the furring wall
// is built against.
function toggleSketchWallFurringSide(id) {
  const wall = sketchWallById(id);
  if (!wall) return false;
  const furring = ensureSketchWallFurring(wall);
  furring.side = furring.side === -1 ? 1 : -1;
  return true;
}

// The Vormauerung's own OUTER (bulge) corner point at one of the wall's true
// node ends — the furring counterpart to sketchWallCornerAtNode, reusing
// the exact same line-intersection approach (via mitered's
// neighborOffsetOverride) so two adjoining Vormauerungen miter cleanly
// against each other at a corner instead of the old rigid parallel-push
// rectangle, which left a gap or an overlapping wedge at any angle other
// than a dead-straight 180° pass-through (see the screenshots that prompted
// this — an acute corner overlapped, an obtuse one left a gap).
//
// A neighbor only participates in the miter if IT also has a Vormauerung
// enabled on the matching face (same `furring.side` — which, like the main
// wall's own `side` parameter, consistently means "the same physical face"
// across a connected chain, not literally "the same numeric flag" in
// isolation). Without that, there is no second bulge to meet here, so the
// neighbor's own plain wall face (offset 0 beyond its half-thickness) is
// used instead — the Vormauerung then simply comes to a clean point at the
// corner, flush with the neighbor's own surface, rather than mitering
// against a bulge that doesn't exist.
//
// A concave (reflex, >180° on the furring's own side) corner needs the
// SAME miter formula — it's not a special case; it just produces a point
// that pulls back short of the node instead of extending past it, which is
// exactly the "shrink instead of overlap" behaviour a concave corner needs.
// But as that reflex angle approaches a dead-straight line from the inside
// (a very common real case — two segments that are almost, but not quite,
// collinear), the two offset lines become nearly parallel and their
// intersection point shoots out an unbounded distance — the classic miter-
// join blowup every vector-graphics stroke implementation has to guard
// against (see SVG's stroke-miterlimit). SKETCH_WALL_FURRING_MITER_LIMIT
// caps that: past a reasonable multiple of the corner's own offset, fall
// back to `selfPoint` (a plain perpendicular end, like an unconnected wall
// tip) instead of a wildly distant point that would render as a huge,
// clearly-wrong overlapping wedge.
//
// That limit MUST be evaluated against the SAME threshold from both walls'
// perspective, not each wall's own offset in isolation — two walls with
// different Vormauerung thicknesses (e.g. a thick gravel-facing on one side
// meeting a thin OSB board on the other) intersect the exact same pair of
// offset lines and so must always land on the exact same raw point, but a
// per-wall threshold could accept that point from the thick wall's side
// (large limit) while rejecting it from the thin wall's side (small limit)
// — one wall then falls back to its own flush `selfPoint` while the other
// keeps the real miter, so the two Vormauerungen no longer share a corner
// point at all and overlap/gap exactly like the un-mitered bug this whole
// function exists to fix. Using the LARGER of the two offsets for the
// limit, from both sides, keeps the accept/reject decision identical
// regardless of which wall is asking.
const SKETCH_WALL_FURRING_MITER_LIMIT = 4;

function sketchWallFurringCornerAtNode(wall, nodeId, getPoint, furring = sketchWallFurring(wall)) {
  const node = getPoint(nodeId);
  const dir = sketchWallDirection(wall, getPoint);
  const side = furring.side === -1 ? -1 : 1;
  const selfOffset = (Number(wall.thickness) || 0) / 2
    + Math.max(1, Number(furring.thickness) || SKETCH_WALL_FURRING_DEFAULT_THICKNESS);
  const leftX = -dir.z;
  const leftZ = dir.x;
  const selfPoint = { x: node.x + side * selfOffset * leftX, z: node.z + side * selfOffset * leftZ };

  const withMiterLimit = (point, otherOffset = selfOffset) => {
    if (!point) return selfPoint;
    const distance = Math.hypot(point.x - node.x, point.z - node.z);
    const limit = Math.max(selfOffset, otherOffset) * SKETCH_WALL_FURRING_MITER_LIMIT;
    return distance <= limit ? point : selfPoint;
  };

  const neighborFurringOffset = neighbor => {
    const neighborFurring = sketchWallFurring(neighbor);
    const neighborHalf = (Number(neighbor.thickness) || 0) / 2;
    if (!neighborFurring.enabled || neighborFurring.side !== side) return neighborHalf;
    return neighborHalf + Math.max(1, Number(neighborFurring.thickness) || SKETCH_WALL_FURRING_DEFAULT_THICKNESS);
  };

  const neighbors = sketchWallsAtNode(nodeId).filter(other => other.id !== wall.id);

  // Mirrors sketchWallCornerAtNode's own straightJoint handling exactly —
  // see there for why the through-pair and stem cases need to be told apart.
  const joint = allSketchStraightJoints().find(entry => entry.nodeId === nodeId);
  if (joint && neighbors.length === 2 && (joint.wallAId === wall.id || joint.wallBId === wall.id)) {
    const throughPartnerId = joint.wallAId === wall.id ? joint.wallBId : joint.wallAId;
    const partner = neighbors.find(other => other.id === throughPartnerId);
    if (partner) {
      const partnerOffset = neighborFurringOffset(partner);
      return withMiterLimit(mitered(selfPoint, dir, node, partner, getPoint, side, partnerOffset), partnerOffset);
    }
    return selfPoint;
  }
  if (joint && neighbors.length === 2) {
    const throughWall = neighbors.find(other => other.id === joint.wallAId || other.id === joint.wallBId);
    if (throughWall) {
      const throughDir = sketchWallDirection(throughWall, getPoint);
      return withMiterLimit(sketchLineIntersection(selfPoint, dir, node, throughDir));
    }
  }

  if (neighbors.length !== 1) return selfPoint;
  const neighbor = neighbors[0];
  const neighborOffset = neighborFurringOffset(neighbor);
  return withMiterLimit(mitered(selfPoint, dir, node, neighbor, getPoint, side, neighborOffset), neighborOffset);
}

// Cross-section quad for the Vormauerung's own added volume: starts at the
// main wall's already-mitered face on furring.side — so it meets the wall
// with no gap or overlap at either end, exactly like the wall's own faces
// already do — and its outer edge is now ALSO mitered at the wall's true
// ends (see sketchWallFurringCornerAtNode), so it meets a neighboring
// wall's own Vormauerung cleanly at a corner too, the same way the wall
// itself already does.
function sketchWallFurringQuad(wall, getPoint, corners = sketchWallCorners(wall, getPoint)) {
  const furring = sketchWallFurring(wall);
  const side = furring.side === -1 ? -1 : 1;
  const innerStart = side === 1 ? corners.startInner : corners.startOuter;
  const innerEnd = side === 1 ? corners.endInner : corners.endOuter;
  const outerStart = sketchWallFurringCornerAtNode(wall, wall.startNodeId, getPoint, furring);
  const outerEnd = sketchWallFurringCornerAtNode(wall, wall.endNodeId, getPoint, furring);
  return [innerStart, innerEnd, outerEnd, outerStart];
}

// Like sketchWallFurringQuad, but for one horizontal sub-span of the
// Vormauerung's own length — needed once the Vormauerung has to be split
// around the wall's Ausschnitte (see sketchWallSolidPieces), since a single
// full-length quad would otherwise sit as an unbroken solid slab in front
// of any opening cut into the wall it's glued to, hiding it from that side.
// Mirrors sketchWallCrossSectionQuad's own start/end handling: mitered
// (both the inner face against the wall, and now the outer face against a
// neighbor's own Vormauerung) at the wall's true ends, a plain perpendicular
// cut everywhere else (an Ausschnitt boundary is never a corner).
function sketchWallFurringCrossSectionQuad(wall, startOffset, endOffset, wallLength, getPoint, corners, furring = sketchWallFurring(wall)) {
  const side = furring.side === -1 ? -1 : 1;
  const dir = sketchWallDirection(wall, getPoint);
  const leftX = -dir.z;
  const leftZ = dir.x;
  const push = side * Math.max(1, Number(furring.thickness) || SKETCH_WALL_FURRING_DEFAULT_THICKNESS);

  const startInner = startOffset <= 0
    ? (side === 1 ? corners.startInner : corners.startOuter)
    : sketchPointAtOffsetOnWall(wall, startOffset, side, getPoint);
  const endInner = endOffset >= wallLength
    ? (side === 1 ? corners.endInner : corners.endOuter)
    : sketchPointAtOffsetOnWall(wall, endOffset, side, getPoint);
  const startOuter = startOffset <= 0
    ? sketchWallFurringCornerAtNode(wall, wall.startNodeId, getPoint, furring)
    : { x: startInner.x + push * leftX, z: startInner.z + push * leftZ };
  const endOuter = endOffset >= wallLength
    ? sketchWallFurringCornerAtNode(wall, wall.endNodeId, getPoint, furring)
    : { x: endInner.x + push * leftX, z: endInner.z + push * leftZ };

  return [
    { ...startInner, u: startOffset },
    { ...endInner, u: endOffset },
    { ...endOuter, u: endOffset },
    { ...startOuter, u: startOffset }
  ];
}

// Keeps the right SketchAngle record(s) for whatever currently touches
// `nodeId`:
// - 2 walls, no straightJoint: the regular "corner" case — one angle
//   between the two walls.
// - 3 walls with a straightJoint locking two of them exactly collinear (a
//   T-Verbindung, created by splitSketchWall): two angles, each between the
//   third ("stem") wall and one side of the straight-through pair. Their
//   values always sum to 180° as a geometric consequence of the joint —
//   defining either one makes the solver mark both as fully determined.
// - anything else (1 wall, 2 walls with a joint = a plain "Schnitt" with no
//   angle to show, 4+ walls): no angle is well-defined, so none is kept.
function syncSketchAnglesAtNode(nodeId) {
  const sw = ensureSketchWallState();
  const walls = sketchWallsAtNode(nodeId);
  const wallIds = new Set(walls.map(wall => wall.id));
  let joint = sw.straightJoints.find(entry => entry.nodeId === nodeId) || null;
  if (joint && (!wallIds.has(joint.wallAId) || !wallIds.has(joint.wallBId))) {
    sw.straightJoints = sw.straightJoints.filter(entry => entry.id !== joint.id);
    joint = null;
  }
  const existing = allSketchAngles().filter(angle => angle.nodeId === nodeId);

  if (walls.length === 3) {
    // A straight joint (from splitSketchWall) says which wall is the
    // "stem" branching off a run meant to stay collinear — that wall is
    // the natural reference for the two angles. Three walls can just as
    // legitimately meet at a shared corner with no such joint (e.g. three
    // separately-drawn walls snapped onto the same existing node); that's
    // still a real T-Verbindung and deserves editable angles too, so fall
    // back to the first wall as the reference instead of showing nothing.
    const stem = joint
      ? walls.find(wall => wall.id !== joint.wallAId && wall.id !== joint.wallBId)
      : walls[0];
    const others = stem ? walls.filter(wall => wall.id !== stem.id) : [];
    if (stem && others.length === 2) {
      const keepIds = [];
      for (const other of others) {
        let entry = existing.find(angle =>
          (angle.wallAId === stem.id && angle.wallBId === other.id)
          || (angle.wallAId === other.id && angle.wallBId === stem.id));
        if (!entry) {
          entry = { id: uid('sketch_angle'), nodeId, wallAId: stem.id, wallBId: other.id, value: null, free: true, conflict: false };
          sw.angles.push(entry);
        }
        keepIds.push(entry.id);
      }
      sw.angles = sw.angles.filter(angle => angle.nodeId !== nodeId || keepIds.includes(angle.id));
      return sw.angles.filter(angle => angle.nodeId === nodeId);
    }
  }

  if (walls.length === 2 && !joint) {
    if (existing.length) return existing[0];
    const angle = {
      id: uid('sketch_angle'),
      nodeId,
      wallAId: walls[0].id,
      wallBId: walls[1].id,
      value: null,
      free: true,
      conflict: false
    };
    sw.angles.push(angle);
    return angle;
  }

  for (const angle of existing) removeSketchAngleById(angle.id);
  return null;
}

function removeSketchAngleById(id) {
  const sw = ensureSketchWallState();
  sw.angles = sw.angles.filter(angle => angle.id !== id);
}

function createSketchStraightJoint(nodeId, wallAId, wallBId) {
  const sw = ensureSketchWallState();
  const joint = { id: uid('sketch_straight_joint'), nodeId, wallAId, wallBId };
  sw.straightJoints.push(joint);
  return joint;
}

function removeSketchStraightJointById(id) {
  const sw = ensureSketchWallState();
  sw.straightJoints = sw.straightJoints.filter(joint => joint.id !== id);
}

function applySketchTJunctionStemAllowance(joint, stemWall) {
  if (!joint || !stemWall) return false;
  const wallA = sketchWallById(joint.wallAId);
  const wallB = sketchWallById(joint.wallBId);
  if (!wallA || !wallB) return false;
  for (const throughWall of [wallA, wallB]) {
    if (!Number.isFinite(throughWall.length) || !throughWall.lengthMode) continue;
    const currentFaceLength = sketchWallSideLength(
      throughWall,
      sketchLiveNodePoint,
      throughWall.lengthMode === 'inner' ? 1 : -1
    );
    updateSketchWallLength(
      throughWall.id,
      Math.max(SKETCH_WALL_MIN_LENGTH, currentFaceLength),
      throughWall.lengthMode
    );
  }
  return true;
}

function applySketchTJunctionStemAllowanceAtNode(nodeId) {
  const joint = allSketchStraightJoints().find(entry => entry.nodeId === nodeId) || null;
  if (!joint) return false;
  const stemWall = sketchWallsAtNode(nodeId).find(wall => (
    wall.id !== joint.wallAId && wall.id !== joint.wallBId
  ));
  return applySketchTJunctionStemAllowance(joint, stemWall);
}

// Splits `wall` into two walls joined by a new node that the solver keeps
// exactly collinear (a hard 'straight' constraint — see sketch_wall_solver.js
// — rather than a regular 180° angle constraint, which is numerically
// unstable right at the wrap boundary of the atan2-based angle
// representation). The seam this creates looks and behaves like one
// continuous wall. Without `splitPoint`, cuts it exactly in half — an odd mm
// total still adds up exactly (1501 -> 750 + 751). With `splitPoint`, cuts
// wherever that point projects onto the wall; this is also how a
// T-Verbindung gets created mid-wall (see handleGlobalSketchWallDrawClick in
// sketch_wall_interaction.js), where the stem's attach point can fall
// anywhere along the wall, not just at its midpoint.
function splitSketchWall(id, splitPoint = null) {
  const wall = sketchWallById(id);
  if (!wall) return null;
  const startNode = sketchNodeById(wall.startNodeId);
  const endNode = sketchNodeById(wall.endNodeId);
  if (!startNode || !endNode) return null;
  const totalLength = sketchNodeDistance(startNode, endNode);
  if (totalLength < SKETCH_WALL_MIN_LENGTH * 2) return null;

  let t = 0.5;
  if (splitPoint) {
    const dx = endNode.x - startNode.x;
    const dz = endNode.z - startNode.z;
    const lenSq = dx * dx + dz * dz || 1;
    t = ((splitPoint.x - startNode.x) * dx + (splitPoint.z - startNode.z) * dz) / lenSq;
    const minT = SKETCH_WALL_MIN_LENGTH / totalLength;
    t = Math.min(Math.max(t, minT), 1 - minT);
  }
  const midX = startNode.x + (endNode.x - startNode.x) * t;
  const midZ = startNode.z + (endNode.z - startNode.z) * t;
  const midNode = createSketchNode(midX, midZ, false);

  const endNodeId = wall.endNodeId;
  const mode = wall.lengthMode === 'outer' || wall.lengthMode === 'inner'
    ? wall.lengthMode
    : null;
  const splitOffset = totalLength * t;
  let targetLen1 = splitPoint ? Math.round(splitOffset) : Math.floor(totalLength / 2);
  let targetLen2 = Math.round(totalLength) - targetLen1;
  if (mode) {
    const side = mode === 'inner' ? 1 : -1;
    const originalStartCorner = sketchWallCornerAtNode(wall, wall.startNodeId, sketchLiveNodePoint, side);
    const originalEndCorner = sketchWallCornerAtNode(wall, wall.endNodeId, sketchLiveNodePoint, side);
    const dir = sketchWallDirection(wall, sketchLiveNodePoint);
    const half = (Number(wall.thickness) || 0) / 2;
    const splitFacePoint = {
      x: midX + side * half * -dir.z,
      z: midZ + side * half * dir.x
    };
    targetLen1 = Math.hypot(splitFacePoint.x - originalStartCorner.x, splitFacePoint.z - originalStartCorner.z);
    targetLen2 = Math.hypot(originalEndCorner.x - splitFacePoint.x, originalEndCorner.z - splitFacePoint.z);
  }
  const wallOptions = {
    thickness: wall.thickness,
    height: wall.height,
    hatchType: wall.hatchType,
    wallColor: wall.wallColor
  };

  // Ausschnitte and the Vormauerung must survive the cut instead of only
  // staying with wallA (the original id) while wallB starts blank. An
  // opening entirely on one side moves wholesale to whichever wall now
  // covers it (offset rebased onto wallB's own local length axis, which
  // starts fresh at the cut); one that straddles the cut point itself is
  // duplicated onto BOTH walls — not deleted, not forced onto just one side
  // — since reclampSketchWallOpenings (run via solveSketchWalls right after
  // this, by every caller) then naturally pulls each copy flush against its
  // own wall's new edge at the cut, which is exactly the physically correct
  // picture of a window sawn in half. The Vormauerung has no offset of its
  // own (it always spans a whole wall), so it's simply copied as-is onto
  // wallB too.
  const openingsForA = [];
  const openingsForB = [];
  for (const opening of allSketchWallOpenings(wall)) {
    const openingStart = opening.offset - opening.width / 2;
    const openingEnd = opening.offset + opening.width / 2;
    if (openingEnd <= splitOffset) {
      openingsForA.push(opening);
    } else if (openingStart >= splitOffset) {
      openingsForB.push({ ...opening, offset: opening.offset - splitOffset });
    } else {
      openingsForA.push(opening);
      openingsForB.push({ ...opening, id: uid('sketch_opening'), offset: opening.offset - splitOffset });
    }
  }
  const furringForB = { ...sketchWallFurring(wall) };

  // Repurpose the original wall as the first half in place — keeps its id
  // (and anything already anchored to its *start* node) untouched — then
  // create a genuinely new wall for the second half.
  wall.endNodeId = midNode.id;
  wall.openings = openingsForA;
  if (mode) updateSketchWallLength(wall.id, targetLen1, mode);
  else updateSketchWallLength(wall.id, null, null);

  const wallB = createSketchWall(midNode.id, endNodeId, wallOptions);
  wallB.openings = openingsForB;
  wallB.furring = furringForB;
  if (mode) updateSketchWallLength(wallB.id, targetLen2, mode);

  const joint = createSketchStraightJoint(midNode.id, wall.id, wallB.id);
  syncSketchAnglesAtNode(midNode.id);
  return { midNode, wallA: wall, wallB, joint };
}

function deleteSketchWall(id) {
  const sw = ensureSketchWallState();
  const wall = sketchWallById(id);
  if (!wall) return false;
  const touchedNodeIds = [wall.startNodeId, wall.endNodeId];
  sw.walls = sw.walls.filter(entry => entry.id !== id);
  sw.angles = sw.angles.filter(angle => angle.wallAId !== id && angle.wallBId !== id);
  sw.straightJoints = sw.straightJoints.filter(joint => joint.wallAId !== id && joint.wallBId !== id);
  for (const nodeId of touchedNodeIds) {
    syncSketchAnglesAtNode(nodeId);
    if (!sketchWallsAtNode(nodeId).length) {
      sw.nodes = sw.nodes.filter(node => node.id !== nodeId);
    }
  }
  if ((S.selection?.sketchWallIds || []).includes(id) || S.selection?.sketchWall?.wallId === id) {
    setSketchWallSelectionIds((S.selection?.sketchWallIds || []).filter(wallId => wallId !== id));
  }
  return true;
}

// A wall-drawing chain's start point exists as a real node the moment the
// user places it (so it can be previewed, snapped onto, etc.) even though no
// wall has been created yet — it only becomes a "real" node once a first
// segment actually connects to it. Cancelling the tool before that (Escape,
// or switching to a different tool mid-placement) must not leave that
// never-used node behind: it has no wall attached, so nothing else
// references it and it's safe to simply drop.
function deleteSketchNodeIfOrphan(nodeId) {
  if (!nodeId) return false;
  const sw = ensureSketchWallState();
  if (!sw.nodes.some(node => node.id === nodeId)) return false;
  if (sketchWallsAtNode(nodeId).length) return false;
  sw.nodes = sw.nodes.filter(node => node.id !== nodeId);
  return true;
}

// Joins two previously-unconnected nodes into one: every wall referencing
// `sourceId` is repointed at `targetId`, `sourceId` is removed, and any
// wall that degenerated into a zero-length self-loop in the process (e.g.
// dragging a wall's loose end onto its own far node) is dropped too — same
// cleanup shape as deleteSketchWall. This is what turns "dragged one wall's
// endpoint next to another wall's endpoint" into a real shared graph node.
function mergeSketchNodes(sourceId, targetId) {
  if (!sourceId || !targetId || sourceId === targetId) return false;
  const source = sketchNodeById(sourceId);
  const target = sketchNodeById(targetId);
  if (!source || !target) return false;
  const sw = ensureSketchWallState();
  for (const wall of sw.walls) {
    if (wall.startNodeId === sourceId) wall.startNodeId = targetId;
    if (wall.endNodeId === sourceId) wall.endNodeId = targetId;
  }
  const degenerateIds = sw.walls.filter(wall => wall.startNodeId === wall.endNodeId).map(wall => wall.id);
  sw.walls = sw.walls.filter(wall => wall.startNodeId !== wall.endNodeId);
  sw.angles = sw.angles.filter(angle =>
    angle.nodeId !== sourceId && !degenerateIds.includes(angle.wallAId) && !degenerateIds.includes(angle.wallBId));
  sw.straightJoints = sw.straightJoints.filter(joint =>
    joint.nodeId !== sourceId && !degenerateIds.includes(joint.wallAId) && !degenerateIds.includes(joint.wallBId));
  sw.nodes = sw.nodes.filter(node => node.id !== sourceId);
  syncSketchAnglesAtNode(targetId);
  if (S.selection?.sketchWall?.nodeId === sourceId) S.selection.sketchWall.nodeId = targetId;
  return true;
}

function updateSketchWallLength(id, length, mode) {
  const wall = sketchWallById(id);
  if (!wall) return false;
  const valid = Number.isFinite(length) && length >= SKETCH_WALL_MIN_LENGTH;
  wall.length = valid ? length : null;
  wall.lengthMode = valid ? mode : null;
  return true;
}

function updateSketchWallThickness(id, thickness) {
  const wall = sketchWallById(id);
  if (!wall) return false;
  if (!(Number(thickness) > 0)) return false;
  wall.thickness = Number(thickness);
  return true;
}

function updateSketchWallHeight(id, height) {
  const wall = sketchWallById(id);
  if (!wall) return false;
  if (!(Number(height) > 0)) return false;
  wall.height = Number(height);
  return true;
}

function updateSketchWallHatchType(id, hatchType) {
  const wall = sketchWallById(id);
  if (!wall) return false;
  wall.hatchType = hatchType || 'beton2';
  return true;
}

function updateSketchWallColor(id, wallColor) {
  const wall = sketchWallById(id);
  if (!wall) return false;
  wall.wallColor = wallColor || null;
  return true;
}

function sketchAngleDisplayDegrees(angle) {
  const measured = sketchDirectedAngleValueAt(angle.nodeId, angle.wallAId, angle.wallBId) * 180 / Math.PI;
  const target = Number.isFinite(angle.value) ? ((angle.value * 180 / Math.PI) % 360 + 360) % 360 : null;
  // Show an authored target only when geometry satisfies the solver tolerance.
  // Free angles use hundredths, without exposing numerical solver noise.
  const degrees = target !== null && Math.abs(measured - target) <= 0.005
    ? target : measured;
  const minor = Math.min(degrees, 360 - degrees);
  return Number(minor.toFixed(target !== null && Math.abs(measured - target) <= 0.005 ? 10 : 2));
}

function updateSketchAngleValueDegrees(id, degrees) {
  const angle = sketchAngleById(id);
  if (!angle) return false;
  angle.value = Number.isFinite(degrees)
    ? sketchExactAngleRadians((degrees * Math.PI) / 180)
    : null;
  return true;
}

// A T-Verbindung's two angles (stem-vs-each side of the straight-through
// pair) always sum to 180° as a geometric consequence of the straightJoint —
// so, same as a wall's mutually-exclusive inner/outer length, only one of
// them may ever hold its own explicit value at a time. Returns the *other*
// angle at the same node when one exists, so callers can clear it whenever
// this one gets set — without that, both sides could end up independently
// fixed to values that no longer sum to 180° after a later edit, and the
// solver would then reject *every* further change to either one as a
// contradiction.
function sketchAnglePairedSiblingId(angleId) {
  const angle = sketchAngleById(angleId);
  if (!angle) return null;
  const sw = ensureSketchWallState();
  const hasJoint = sw.straightJoints.some(joint => joint.nodeId === angle.nodeId);
  if (!hasJoint) return null;
  const sibling = sw.angles.find(other => other.nodeId === angle.nodeId && other.id !== angle.id);
  return sibling ? sibling.id : null;
}

function setSketchWallAxis(id, axis) {
  const wall = sketchWallById(id);
  if (!wall) return false;
  if (axis === 'horizontal') {
    wall.horizontal = !wall.horizontal;
    if (wall.horizontal) wall.vertical = false;
  } else if (axis === 'vertical') {
    wall.vertical = !wall.vertical;
    if (wall.vertical) wall.horizontal = false;
  }
  return true;
}

// Marks `wall`'s absolute direction as pinned, capturing its current angle
// as the target the solver must hold from now on (see the 'fixedAngle'
// constraint in sketch_wall_solver.js). This is what lets one wall anchor
// the whole sketch's rotation — without it, nothing stops the least-squares
// solve from quietly rotating the entire sketch a little on every edit.
function toggleSketchWallFixed(id) {
  const wall = sketchWallById(id);
  if (!wall) return false;
  if (!wall.fixed) {
    // An explicitly chosen reference supersedes the automatic chain reference.
    // Keep authored dimensions and unrelated components; release only auto pins.
    const connected = sketchWallConnectedNodeIds(wall);
    for (const other of allSketchWalls()) {
      if (!connected.has(other.startNodeId) || other.fixedSource !== 'chain') continue;
      for (const nodeId of [other.startNodeId, other.endNodeId]) {
        const node = sketchNodeById(nodeId);
        if (node) node.fixed = false;
      }
      other.fixed = false; other.fixedAngle = null; other.fixedSource = null;
    }
  }
  wall.fixed = !wall.fixed;
  wall.fixedSource = wall.fixed ? 'user' : null;
  wall.fixedAngle = wall.fixed
    ? sketchExactAngleRadians(sketchWallAbsoluteAngle(wall))
    : null;
  return true;
}

// Which of the 3 sidebar cards (Parametrische Wände / Ausschnitte /
// Vormauerung) an accordion-style selection should bring forward — an
// opening wins over furring wins over the plain wall body, so selecting an
// opening or the furring body (see bindSketchWallInteraction's `furring`
// flag) always surfaces its own card instead of leaving whichever card was
// open before untouched.
function sketchWallSidebarCardForSelection(patch) {
  if (patch?.openingId) return 'openings';
  if (patch?.furring) return 'furring';
  return 'main';
}

function clearSketchWallSelection() {
  ensureSketchWallState();
  S.selection.sketchWall = { nodeId: null, wallId: null, angleId: null, openingId: null, furring: false };
  S.selection.sketchWallIds = [];
  S.selection.items = (S.selection.items || []).filter(item => item.owner !== SKETCH_WALL_SYSTEM_ID);
}

function selectSketchWallEntity(patch) {
  ensureSketchWallState();
  S.selection.sketchWall = { nodeId: null, wallId: null, angleId: null, openingId: null, furring: false, ...patch };
  const wallIds = patch?.wallId ? [patch.wallId] : [];
  S.selection.sketchWallIds = wallIds;
  S.selection.items = (S.selection.items || []).filter(item => item.owner !== SKETCH_WALL_SYSTEM_ID);
  for (const wallId of wallIds) {
    S.selection.items.push({ owner: SKETCH_WALL_SYSTEM_ID, kind: 'wall', id: wallId });
  }
  if (patch?.wallId) S.ui.sketchWallActiveSidebarCard = sketchWallSidebarCardForSelection(patch);
}

function setSketchWallSelectionIds(ids = []) {
  ensureSketchWallState();
  const validIds = new Set(allSketchWalls().map(wall => wall.id));
  const wallIds = [...new Set((Array.isArray(ids) ? ids : [ids])
    .map(id => String(id || ''))
    .filter(id => validIds.has(id)))];
  const primaryWallId = wallIds.at(-1) || null;
  S.selection.sketchWallIds = wallIds;
  S.selection.sketchWall = {
    nodeId: null,
    wallId: primaryWallId,
    angleId: null,
    openingId: null,
    furring: false
  };
  S.selection.items = (S.selection.items || []).filter(item => item.owner !== SKETCH_WALL_SYSTEM_ID);
  for (const wallId of wallIds) {
    S.selection.items.push({ owner: SKETCH_WALL_SYSTEM_ID, kind: 'wall', id: wallId });
  }
  if (primaryWallId) S.ui.sketchWallActiveSidebarCard = 'main';
  return wallIds;
}

function sketchWallIsSelected(id) {
  ensureSketchWallState();
  return (S.selection.sketchWallIds || []).includes(id)
    || S.selection.sketchWall?.wallId === id;
}

function sketchWallBoundsPoint(nodeId) {
  const node = sketchNodeById(nodeId);
  return node ? { x: node.x, z: node.z } : { x: 0, z: 0 };
}

// Feeds the scene's project-object bounds (see scene3d.js's
// collectObstacleProjectObjects / calculateBoundsFromInstances) so the
// floor/ceiling plane grows to fit drawn sketch walls exactly like it
// already does for placed systems (Multischrank, Saniguard, ...).
function collectSketchWallProjectObjects() {
  ensureSketchWallState();
  return allSketchWalls().map(wall => {
    const corners = sketchWallCorners(wall, sketchWallBoundsPoint);
    const xs = [corners.startInner.x, corners.endInner.x, corners.startOuter.x, corners.endOuter.x];
    const zs = [corners.startInner.z, corners.endInner.z, corners.startOuter.z, corners.endOuter.z];
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minZ = Math.min(...zs);
    const maxZ = Math.max(...zs);
    return {
      id: wall.id,
      systemId: SKETCH_WALL_SYSTEM_ID,
      x: (minX + maxX) / 2,
      z: (minZ + maxZ) / 2,
      width: Math.max(1, maxX - minX),
      depth: Math.max(1, maxZ - minZ),
      height: Math.max(1, Number(wall.height) || 0),
      bounds: { minX, maxX, minZ, maxZ }
    };
  });
}

export {
  SKETCH_WALL_SYSTEM_ID,
  SKETCH_WALL_DEFAULT_THICKNESS,
  SKETCH_WALL_DEFAULT_HEIGHT,
  SKETCH_WALL_MIN_LENGTH,
  SKETCH_WALL_JOIN_TOLERANCE,
  ensureSketchWallState,
  createSketchWallReadContext,
  sketchExactAngleRadians,
  normalizeSketchWallStoredAngles,
  allSketchNodes,
  allSketchWalls,
  allSketchAngles,
  allSketchStraightJoints,
  sketchNodeById,
  sketchWallById,
  sketchAngleById,
  sketchWallsAtNode,
  sketchWallOtherNodeId,
  sketchWallConnectedNodeIds,
  sketchNodeDistance,
  sketchWallLength,
  sketchWallAbsoluteAngle,
  sketchWallCornerAtNode,
  sketchWallSideLength,
  sketchWallCorners,
  sketchAngleValueAt,
  sketchDirectedAngleValueAt,
  findNearestSketchNode,
  findNearestSketchWallPoint,
  createSketchNode,
  createSketchWall,
  createSketchStraightJoint,
  removeSketchStraightJointById,
  applySketchTJunctionStemAllowance,
  applySketchTJunctionStemAllowanceAtNode,
  splitSketchWall,
  syncSketchAnglesAtNode,
  deleteSketchWall,
  deleteSketchNodeIfOrphan,
  mergeSketchNodes,
  updateSketchWallLength,
  updateSketchWallThickness,
  updateSketchWallHeight,
  updateSketchWallHatchType,
  updateSketchWallColor,
  updateSketchAngleValueDegrees,
  sketchAngleDisplayDegrees,
  sketchAnglePairedSiblingId,
  toggleSketchWallFixed,
  setSketchWallAxis,
  clearSketchWallSelection,
  selectSketchWallEntity,
  setSketchWallSelectionIds,
  sketchWallIsSelected,
  collectSketchWallProjectObjects,
  sketchWallDrawDefaults,
  updateSketchWallDrawDefaults,
  sketchPointAtOffsetOnWall,
  allSketchWallOpenings,
  sketchWallOpeningsWithLabels,
  sketchWallOpeningClampedOffset,
  reclampSketchWallOpenings,
  createSketchWallOpening,
  sketchWallOpeningById,
  updateSketchWallOpening,
  deleteSketchWallOpening,
  sketchWallOpeningRegions,
  sketchWallSolidPieces,
  SKETCH_WALL_OPENING_PRESETS,
  SKETCH_WALL_FURRING_DEFAULT_THICKNESS,
  SKETCH_WALL_FURRING_DEFAULT_HEIGHT,
  sketchWallFurring,
  setSketchWallFurringEnabled,
  updateSketchWallFurring,
  updateSketchWallFurringColor,
  matchSketchWallFurringToMainWall,
  toggleSketchWallFurringSide,
  sketchWallFurringQuad,
  sketchWallFurringCrossSectionQuad,
  sketchWallFurringCornerAtNode
};
